import { useState, useEffect } from 'react';
import { Box, Typography, Container, useTheme, Button } from '@mui/material';
import SliderArtistsImage1 from '../../assets/sliderArtists/sliderArtistsImage1.png';
import SliderArtistsImage2 from '../../assets/sliderArtists/sliderArtistsImage2.png';
import SliderArtistsImage3 from '../../assets/sliderArtists/sliderArtistsImage3.png';
import ListArtstyleButton from './SLPArtistsListArtstyleButton';

const heroImages = [SliderArtistsImage1, SliderArtistsImage2, SliderArtistsImage3];

export const SLPArtistsHero = () => {
  const theme = useTheme();

  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [fade, setFade] = useState(true);

  useEffect(() => {
    const interval = setInterval(() => {
      setFade(false);
      setTimeout(() => {
        setCurrentImageIndex(prevIndex => (prevIndex + 1) % heroImages.length);
        setFade(true);
      }, 500);
    }, 5000);

    return () => clearInterval(interval);
  }, []);

  return (
    <>
      {/* Main Hero Section */}
      <Box
        sx={{
          width: '100%',
          height: '88vh',
          overflow: 'hidden',
          position: 'relative',
          [theme.breakpoints.down('md')]: {
            height: { xs: '20vh', md: '20vh', lg: '40vh' }
          },
          id: 'home'
        }}
      >
        <Box
          sx={{
            width: '100%',
            height: '100%',
            position: 'absolute',
            top: 0,
            left: 0,
            backgroundImage: `url(${heroImages[currentImageIndex]})`,
            backgroundSize: 'cover',
            backgroundPosition: 'center',
            transition: 'opacity 0.8s ease-in-out',
            opacity: fade ? 1 : 0,
            zIndex: 1,
            '&::after': {
              content: '""',
              position: 'absolute',
              bottom: 0,
              left: 0,
              width: '100%',
              height: '100%',
              background: 'linear-gradient(60deg, rgba(0, 0, 0, 0.9) 27%, transparent 80%)',
              pointerEvents: 'none'
            }
          }}
        />
        <Typography
          variant="h2"
          component="h1"
          sx={{
            display: { xs: 'block', md: 'none' }, // ✅ Only on mobile
            position: 'absolute',
            bottom: '62.8px',
            left: '1rem',
            right: '1rem',
            zIndex: 2,
            color: '#ffffff',
            fontWeight: 500,
            fontSize: '20px',
            fontFamily: 'Inter',
            lineHeight: '130%',
            textAlign: 'center',
            padding: '0.75rem 1rem',
            borderRadius: '8px'
          }}
        >
          Turn Your Art Style Into Passive Income On Your Terms
        </Typography>

        {/* Desktop Content */}
        <Container
          maxWidth="xl"
          sx={{
            position: 'relative',
            height: '100%',
            zIndex: 2,
            display: 'flex',
            alignItems: 'end',
            [theme.breakpoints.down('md')]: {
              display: 'none' // Hide on mobile
            }
          }}
        >
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              gap: '1rem',
              marginBottom: '103px',
              px: { xs: 9, md: 7 }
            }}
          >
            <Typography
              variant="h2"
              component="h1"
              width="452px"
              fontFamily="Inter"
              sx={{
                fontFamily: 'Inter',
                fontWeight: 500,
                fontSize: { xs: '1.5rem', sm: '1.5rem', md: '28px' },
                lineHeight: '120%',
                color: '#ffffff',
                display: { xs: 'none', md: 'block' },
                mb: 1
              }}
            >
              Turn Your Art Style Into Passive Income On Your Terms{' '}
            </Typography>
            <Typography
              sx={{
                fontWeight: 300,
                color: '#C0C0C0',
                fontSize: { xs: '1rem', sm: '1rem', md: '16px' },
                fontFamily: 'Inter',
                width: '420px',
                lineHeight: '120%'
              }}
            >
              Saagas empowers visual artists to license their unique style for use in generated imagery. You retain full rights, control how your art work is
              used, and earn on every download.
            </Typography>
            <Box
              sx={{
                display: 'flex',
                gap: 2,
                flexWrap: 'nowrap' // This ensures buttons stay side by side
              }}
            >
              <Button
                sx={{
                  fontFamily: 'Inter',
                  maxHeight: '52px',
                  maxWidth: '200px',
                  marginTop: '5px',
                  color: '#fff',
                  fontWeight: 600,
                  fontSize: '1rem',
                  padding: '16px 24px',
                  borderRadius: '999px',
                  border: '1px solid rgba(255, 255, 255, 0.5)',
                  backgroundColor: 'transparent',
                  textTransform: 'none',
                  transition: 'all 0.3s ease-in-out',
                  '&:hover': {
                    backgroundColor: 'rgba(255, 255, 255, 0.1)',
                    border: '1px solid rgba(255, 255, 255, 0.8)',
                    scrollBehavior: 'smooth'
                  }
                }}
                onClick={e => {
                  e.preventDefault();
                  const section = document.querySelector('#how-it-works');
                  section?.scrollIntoView({ behavior: 'smooth' });
                }}
              >
                See how it works
              </Button>

              <ListArtstyleButton maxHeight="52px" maxWidth="200px" marginTop="4px" />
            </Box>
          </Box>
        </Container>
      </Box>

      {/* Mobile Content Section - Black Background */}
      <Box
        sx={{
          display: 'none',
          [theme.breakpoints.down('md')]: {
            display: 'flex',
            backgroundColor: '#000000',
            paddingTop: '2rem',
            paddingBottom: { xs: '0rem', md: '2rem', lg: '2rem' },
            paddingLeft: '1.5rem',
            paddingRight: '1.5rem',
            justifyContent: 'center',
            alignItems: 'center'
          }
        }}
      >
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            gap: '1.5rem',
            alignItems: 'center',
            textAlign: 'center',
            maxWidth: '600px',
            width: '100%'
          }}
        >
          <Typography
            sx={{
              fontWeight: 300,
              color: '#C0C0C0',
              fontSize: '16px',
              fontFamily: 'Inter',
              lineHeight: '140%',
              width: '302px'
            }}
          >
            Saagas empowers visual artists to license their unique style for use in generated imagery. You retain full rights, control how your art work is
            used, and earn on every download.
          </Typography>
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'center',
              gap: 2,
              flexDirection: 'row',
              alignItems: 'center',
              flexWrap: 'nowrap',
              width: '100%'
            }}
          >
            <Button
              sx={{
                flex: '1 0 auto',
                maxWidth: '200px',
                color: '#fff',
                fontWeight: 600,
                fontSize: '1rem',
                padding: { xs: '12px 16px', md: '16px 24px' },
                borderRadius: '999px',
                border: '1px solid rgba(255, 255, 255, 0.5)',
                backgroundColor: 'transparent',
                fontFamily: 'Inter',
                textTransform: 'none',
                transition: 'all 0.3s ease-in-out',
                '&:hover': {
                  backgroundColor: 'rgba(255, 255, 255, 0.1)',
                  border: '1px solid rgba(255, 255, 255, 0.8)'
                }
              }}
              onClick={e => {
                e.preventDefault();
                const section = document.querySelector('#how-it-works');
                section?.scrollIntoView({ behavior: 'smooth' });
              }}
            >
              See how it works
            </Button>

            <Box sx={{ flex: '1 0 auto', maxWidth: '200px' }}>
              <ListArtstyleButton maxHeight="52px" maxWidth="100%" />
            </Box>
          </Box>
        </Box>
      </Box>
    </>
  );
};
